import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StarIcon from '@mui/icons-material/Star';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import {
  Button, LinearProgress, TextField, Switch, Alert, AlertTitle,
  Paper, Typography, Box, Modal, Tooltip,
} from '@mui/material';
import PortalAutocomplete from './PortalAutocomplete';
import {WithContext as ReactTags} from 'react-tag-input'
import { TERMS } from '../terms';
import '../MainScreen.css';
import {useNavigate} from 'react-router-dom';

const isLocal = window.location.hostname === 'localhost';
const apiPrefix = isLocal ? '' : '/api';

const suggestions = TERMS.map((term) => {
  return {
    id: term,
    text: term,
  };
});

const KeyCodes = {
  comma: 188,
  enter: 13,
};
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const SectionHeader = ({ title, children }) => (
  <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem', display: 'inline-block', mb: 1 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const modalPaperSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  borderRadius: 2,
  p: 4,
};

function MainScreen() {

  const navigate = useNavigate();
  const { user } = useAuth();

  const allTags = [];

  const [userId, setUserId] = useState('');
  const [tags, setTags] = React.useState(allTags);
  const [allowedPortals, setAllowedPortals] = useState([]);
  const [selectedPortals, setSelectedPortals] = useState([]);

  useEffect(() => { document.title = 'Pretraga uživo | Media Scanner'; }, []);

  useEffect(() => {
    if (!user) return;
    const currentUserId = user.user_id;
    setUserId(currentUserId);

    const fetchData = async () => {
      try {
        const response = await axios.post(apiPrefix + '/get_terms_by_userid', {
          user_id: currentUserId,
        }, {
          withCredentials: true,
        });
        setTags(response.data.map(tag => ({
          id: String(tag.id ?? tag.text),
          text: String(tag.text)
        })));
      } catch (error) {
        console.error('Error getting terms by user_id:', error);
      }
    };

    const fetchAllowedPortals = async () => {
      try {
        const response = await axios.post(apiPrefix + '/get_allowed_portals_for_user_id', {
          user_id: currentUserId,
        }, {
          withCredentials: true,
        });
        const returnedAllowedPortals = response.data.map(portal => ({
          id: portal.id,
          name: portal.name,
          url: portal.url,
          source_name: portal.source_name
        })).sort((a, b) => a.name.localeCompare(b.name));
        setAllowedPortals(returnedAllowedPortals);

        // Fetch default portals
        try {
          const defaultsResponse = await axios.post(apiPrefix + '/get_default_portals_by_userid', {
            user_id: currentUserId,
          }, { withCredentials: true });

          if (defaultsResponse.data.length > 0) {
            const defaultIds = new Set(defaultsResponse.data.map(p => p.id));
            const defaultSelection = returnedAllowedPortals.filter(p => defaultIds.has(p.id));
            setSelectedPortals(defaultSelection);
            setSavedDefaultPortals(defaultSelection);
          } else {
            setSelectedPortals(returnedAllowedPortals);
            setSavedDefaultPortals([]);
          }
        } catch (defaultsError) {
          console.error('Error getting default portals:', defaultsError);
          setSelectedPortals(returnedAllowedPortals);
          setSavedDefaultPortals([]);
        }
      } catch (error) {
        console.error('Error getting allowed portals:', error);
      }
    };

    fetchData();
    fetchAllowedPortals();
  }, [user]); // re-run if user changes

  const fetchData = async (userId) => {
    try {
      const response = await axios.post(apiPrefix + '/get_terms_by_userid', {
        user_id: userId,
      }, {
        withCredentials: true,
      });
      setTags(response.data.map(tag => ({
        id: String(tag.id ?? tag.text), // koristi id ako postoji, inače text
        text: String(tag.text)
      })));
      return userId;
    } catch (error) {
      console.error('Error getting terms by user_id:', error);
    }
  };



  const [formData, setFormData] = useState('');   // use this to store findings
  const [responseMessage, setResponseMessage] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [successfullAPICalls, setSuccessfullAPICalls] = React.useState(0);
  const [sourcesListLength, setSourcesListLength] = React.useState(0);
  const [sourceInSearch, setSourceInSearch] = React.useState('');
  const [allFindings, setAllFindings] = useState([]);
  //const [allItems, setAllItems] = useState('');
  const [open, setModalOpen] = React.useState(false);
  //const [snackOpen, setSnackOpen] = React.useState(false);
  const [num, setNum] = React.useState(24);

  const [switchChecked, setSwitchChecked] = React.useState(true);

  const handleSwitchChange = (event) => {
    setSwitchChecked(event.target.checked);
    if (event.target.checked) {
      fetchData(userId);
    } else {
      setTags([])
    }
  };

  const [termSetChangeHappened, setTermSetChangeHappened] = useState(false);
  const [confirmSaveModalOpen, setConfirmSaveModalOpen] = useState(false); // confirmation modal

  const [portalSetChangeHappened, setPortalSetChangeHappened] = useState(false);
  const [confirmSavePortalsModalOpen, setConfirmSavePortalsModalOpen] = useState(false);
  const [savedDefaultPortals, setSavedDefaultPortals] = useState([]);

  const handleDelete = (i) => {
    setTags(tags.filter((tag, index) => index !== i));
    setTermSetChangeHappened(true);
  };

  const handleAddition = (tag) => {
    setTags([...tags, { id: String(tag.id ?? tag.text), text: String(tag.text) }]);
    setTermSetChangeHappened(true);
  };

  const handleDrag = (tag, currPos, newPos) => {
    const newTags = tags.slice();

    newTags.splice(currPos, 1);
    newTags.splice(newPos, 0, tag);

    // re-render
    setTags(newTags);
  };

  const handleTagClick = (index) => {};


  const [errorMessage, setErrorMessage] = useState("");
  ///////////////////////////////////////////////////////
  ///////////  SUBMIT one by one - version 2  ///////////
  ///////////////////////////////////////////////////////
  const handleSubmit3 = async (event) => {
    event.preventDefault();
    setSearchStatus("Initialized");
    setModalOpen(true)

    lemmatize_terms();

  }

  function makeRecursiveApiCall(sources, payload, successCallsCounter, lenSources, findings) {
      setSuccessfullAPICalls(prevSuccessfullAPICalls => prevSuccessfullAPICalls + 1);
      if (sources.length === 0) {
          setSourceInSearch('');
          navigate('/resultFilter', {state: {findings}});
          return findings;
      } else {
          const nextSource = sources.shift();
          var payload2 = {
            "source": nextSource,
            "terms": payload.terms,
            "freshness": payload.freshness,
          }
          // in allowedPortals find the name of the portal for the source == nextSource
          for (let i=0; i<allowedPortals.length; i++) {
            if (allowedPortals[i].source_name === nextSource) {
              // set the sourceInSearch to allowedPortals[i].portalName
              setSourceInSearch(allowedPortals[i].name);
              break;
            }
          }

          return axios.post(apiPrefix + '/searchTermInArticles', payload2)
            .then((response) => {
              if (response.status === 200) {
                successCallsCounter = successCallsCounter + 1;
                for (let i=0; i<response.data.articles.length; i++) {
                  findings.push(response.data.articles[i]);
                }
              }
              return makeRecursiveApiCall(sources, payload2, successCallsCounter, lenSources, findings);
            })
            .catch((error) => {
              console.error("Error in API call:", error);
              setSearchStatus("Failed");
              setErrorMessage('');
            });
      }
  }

  function preparePayload() {
    var payload = {
      sources: selectedPortals.map(p => p.source_name),
      terms: tags.map(({ text }) => text),
      freshness: +num,
    };
    return payload;
  }

  const [lemmatized_terms, setLemmatized_terms] = useState("");

  const lemmatize_terms = async () => {
    var terms = tags.map(({ text }) => text);
    var payload = {
      "terms": terms
    }
    // call API on the backend and wait for the response
    setSearchStatus("Initializing");
    try {
      const response = await axios.post(apiPrefix + '/lemmatize_terms', { payload });
      if (response.status === 200) {
        setLemmatized_terms(response.data.lemmatized_terms);
        setSearchStatus("Initialized");
      }
    } catch (error) {
      console.error("Error in API call to <lemmatize_terms>:", error);
      setSearchStatus("Failed");
      setErrorMessage('');
    }
  };

  useEffect(() => {
    if (lemmatized_terms) {
      var payload = preparePayload();
      var sources = payload.sources;
      var payload2 = {
        "source": null,
        "terms": lemmatized_terms, // ovo je već lista stringova
        "freshness": payload.freshness,
      };
      var findings = [];
      var lenSources = sources.length;
      setSourcesListLength(lenSources);

      var successCallsCounter = 0;
      setSuccessfullAPICalls(0);
      makeRecursiveApiCall(sources, payload2, successCallsCounter, lenSources, findings);
    }
  }, [lemmatized_terms]);



  function searchButtonEnabled() {
    return selectedPortals.length > 0 && tags.length > 0;
  }

  function handleSetDefaultUserTerms() {
    // collect allTags and make a list of string names from text properties
    var defaultUserTerms = [];
    for (let i=0; i<tags.length; i++) {
      defaultUserTerms.push(tags[i].text);
    }
    // call /setDefaultUserTerms with POST on backend
    axios.post(apiPrefix + '/setDefaultUserTerms', {'list_new_terms': defaultUserTerms})
    .then((response) => {
      if (response.status === 200) {
        setTermSetChangeHappened(false);
      } else {
        setTermSetChangeHappened(true);
        console.error("Error in API call:", response.data);
      }
    })
  }

  function handleSetDefaultUserPortals() {
    const portalIds = selectedPortals.map(p => p.id);
    axios.post(apiPrefix + '/setDefaultUserPortals', { list_portal_ids: portalIds }, { withCredentials: true })
      .then((response) => {
        if (response.status === 200) {
          setPortalSetChangeHappened(false);
          setSavedDefaultPortals([...selectedPortals]);
        } else {
          console.error("Error in API call:", response.data);
        }
      });
  }

  function handleCloseModal(){
    setModalOpen(false);
    setResponseMessage('');
    setSearchStatus('');
    setSuccessfullAPICalls(0);
  }

  const searchModalContent = {
    Initializing: (
      <Paper elevation={8} sx={modalPaperSx}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 2 }}>Pripremam pretragu ...</Typography>
        <LinearProgress />
      </Paper>
    ),
    Initialized: (
      <Paper elevation={8} sx={modalPaperSx}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 2 }}>
          Pretražujem {sourceInSearch}... ({successfullAPICalls}/{sourcesListLength})
        </Typography>
        <LinearProgress />
      </Paper>
    ),
    Failed: (
      <Paper elevation={8} sx={modalPaperSx}>
        <Alert severity="error">
          <AlertTitle>Pretraga nije uspjela</AlertTitle>
          Došlo je do pogreške pri pretraživanju portala {sourceInSearch}. Molimo pokušajte ponovo.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="outlined" onClick={handleCloseModal}>Zatvori</Button>
        </Box>
      </Paper>
    ),
    Partial: (
      <Paper elevation={8} sx={modalPaperSx}>
        <Alert severity="warning">
          <AlertTitle>Djelomično</AlertTitle>
          Pretraga je bila djelomično uspješna
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="outlined" onClick={handleCloseModal}>Zatvori</Button>
        </Box>
      </Paper>
    ),
    Complete: (
      <Paper elevation={8} sx={modalPaperSx}>
        <Alert severity="success">
          <AlertTitle>Gotovo!</AlertTitle>
          Pretraga je završila — <strong>Rezultati su u inboxu!</strong>
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="outlined" onClick={handleCloseModal}>Zatvori</Button>
        </Box>
      </Paper>
    ),
  }[searchStatus] || (
    <Paper elevation={8} sx={modalPaperSx}>
      <Alert severity="error">
        <AlertTitle>Dogodila se nepoznata greška</AlertTitle>
      </Alert>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" onClick={handleCloseModal}>Zatvori</Button>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <form onSubmit={handleSubmit3}>
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
          {/* Page header */}
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 3 }}>
            Pretraga uživo
          </Typography>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            {/* Portals */}
            {allowedPortals.length > 0 && (
              <>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem', display: 'inline-block' }}
                  >
                    Portali
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={() => { setSelectedPortals(allowedPortals); setPortalSetChangeHappened(true); }}
                    disabled={selectedPortals.length === allowedPortals.length}
                    sx={{ borderRadius: '20px', textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                  >
                    Dodaj sve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disableElevation
                    onClick={() => { setSelectedPortals([]); setPortalSetChangeHappened(true); }}
                    disabled={selectedPortals.length === 0}
                    sx={{ borderRadius: '20px', textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                  >
                    Ukloni sve
                  </Button>
                </Box>
                <PortalAutocomplete
                  options={allowedPortals}
                  value={selectedPortals}
                  isOptionEqualToValue={(opt, val) => opt.source_name === val.source_name}
                  onChange={(_, values) => { setSelectedPortals(values); setPortalSetChangeHappened(true); }}
                />
                <Box sx={{ mt: 1.5 }}>
                  <Tooltip title="Spremi trenutni odabir portala kao zadani za buduće pretrage">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<StarIcon />}
                        onClick={() => setConfirmSavePortalsModalOpen(true)}
                        disabled={!portalSetChangeHappened}
                        sx={{ textTransform: 'none' }}
                      >
                        Zapamti portale
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}

            {/* Terms */}
            <SectionHeader title="Traženi pojmovi" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Koristi spremljene pojmove:
              </Typography>
              <Switch
                size="small"
                onChange={handleSwitchChange}
                checked={switchChecked}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </Box>
            <ReactTags
              tags={tags}
              suggestions={suggestions}
              delimiters={delimiters}
              handleDelete={handleDelete}
              handleAddition={handleAddition}
              handleDrag={handleDrag}
              handleTagClick={handleTagClick}
              inputFieldPosition="bottom"
              placeholder='Unesi pojam, zatim zarez ili Enter'
              autocomplete
              editable
            />
            <Box sx={{ mt: 1.5 }}>
              <Tooltip title="Spremi trenutne pojmove kao zadane za buduće pretrage">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<StarIcon />}
                    onClick={() => setConfirmSaveModalOpen(true)}
                    disabled={!termSetChangeHappened}
                    sx={{ textTransform: 'none' }}
                  >
                    Zapamti pojmove
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {/* Freshness */}
            <SectionHeader title="Objavljeno prije (h)" />
            <TextField
              type="number"
              size="small"
              sx={{ width: 120 }}
              variant="outlined"
              onChange={(e) => setNum(e.target.value)}
              value={num}
            />

            {/* Search button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
              <Button
                variant="contained"
                type="submit"
                disabled={!searchButtonEnabled()}
                startIcon={<FindInPageIcon />}
                size="large"
              >
                Traži
              </Button>
            </Box>
          </Paper>
        </Box>
      </form>

      {/* Search progress modal */}
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        {searchModalContent}
      </Modal>

      {/* Confirm save terms modal */}
      <Modal
        open={confirmSaveModalOpen}
        onClose={() => setConfirmSaveModalOpen(false)}
        aria-labelledby="confirm-modal-title"
      >
        <Paper elevation={8} sx={modalPaperSx}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Promjena pojmova
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Jeste li sigurni da želite promijeniti spremljene pojmove?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                fetchData(userId);
                setTermSetChangeHappened(false);
                setConfirmSaveModalOpen(false);
              }}
            >
              Odustani
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleSetDefaultUserTerms();
                setConfirmSaveModalOpen(false);
              }}
            >
              Da, promijeni
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Confirm save portals modal */}
      <Modal
        open={confirmSavePortalsModalOpen}
        onClose={() => setConfirmSavePortalsModalOpen(false)}
        aria-labelledby="confirm-portals-modal-title"
      >
        <Paper elevation={8} sx={modalPaperSx}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Promjena portala
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Jeste li sigurni da želite promijeniti spremljene portale?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                if (savedDefaultPortals.length > 0) {
                  setSelectedPortals(savedDefaultPortals);
                } else {
                  setSelectedPortals(allowedPortals);
                }
                setPortalSetChangeHappened(false);
                setConfirmSavePortalsModalOpen(false);
              }}
            >
              Odustani
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleSetDefaultUserPortals();
                setConfirmSavePortalsModalOpen(false);
              }}
            >
              Da, promijeni
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
}

export default MainScreen;
